import React from 'react';

import { useRecoilValue } from 'recoil';

import { formatDateAndTimeZ, ia, iaRa, isEmpty } from 'lib/helpers/utility';
import { useDocumentSign } from 'lib/hooks/queries/documentSign/useDocumentSign';

import { currentPractice } from 'components/practice/practiceState';
import { clinicalNote as clinicalNoteState } from 'components/state';

import { processData } from '../../../shared/helpers/documentSignatureHelpers';

const ProviderSignature = ({ customClinicalNote = null }) => {
  let clinicalNote = useRecoilValue(clinicalNoteState);
  if (customClinicalNote) {
    clinicalNote = customClinicalNote?.clinicalNote;
  }

  const { data: signaturesDocument } = useDocumentSign({
    params: {
      documentIds: [clinicalNote?.id],
      withRelations: { user: true }
    },
    dependencies: [clinicalNote?.id],
    options: {
      enabled: !!clinicalNote?.id,
      select: (response) => {
        if (clinicalNote?.locked) return processData(response);
        return iaRa(response?.data);
      }
    }
  });

  return (
    <div className="grid gap-2 bg-white !px-6 !py-4">
      <div className="text-base font-medium leading-normal text-neutral-800">Signatures</div>
      <div className="flex flex-wrap !gap-3">
        {ia(signaturesDocument) ? (
          clinicalNote?.locked ? (
            <LockedSignatures signaturesDocument={signaturesDocument} />
          ) : (
            <SignatureProgress signaturesDocument={signaturesDocument} />
          )
        ) : (
          <p className="text-neutral-800">No signatures found on the clinical note</p>
        )}
      </div>
    </div>
  );
};

const LockedSignatures = ({ signaturesDocument }) => {
  const practice = useRecoilValue(currentPractice);

  const printStyles = {
    container: {
      marginTop: '12px',
      pageBreakInside: 'avoid'
    },
    roleText: {
      marginBottom: '8px',
      fontSize: '16px',
      fontWeight: '500',
      color: '#374145' // text-neutral-800
    },
    signatureBox: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '260px',
      gap: '24px',
      padding: '24px',
      border: '1px solid #B0EAFF', // border-primary-100
      backgroundColor: '#E6F8FF', // bg-primary-50
      borderRadius: '6px'
    },
    textInfo: {
      fontSize: '14px',
      color: '#374145', // text-neutral-800
      textAlign: 'center',
      margin: '0',
      padding: '2px 0'
    }
  };

  return signaturesDocument?.map((row, index) => {
    return (
      <div key={index} style={printStyles.container}>
        <div style={printStyles.roleText}>{row?.role}</div>
        <div style={printStyles.signatureBox}>
          <div style={{ textAlign: 'center' }}>
            {row?.signature ? (
              <img
                style={{ width: '128px', height: '128px', objectFit: 'contain' }}
                src={row?.signature}
                alt="Signature on file"
              />
            ) : (
              <p>SIGNATURE ON FILE</p>
            )}
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
            <div style={printStyles.textInfo}>{row?.signedBy}</div>
            <div style={printStyles.textInfo}>{row?.initials}</div>
            <div style={printStyles.textInfo}>
              {row?.date ? formatDateAndTimeZ(row?.date, practice?.timezone) : 'N/A'}
            </div>
          </div>
        </div>
      </div>
    );
  });
};

const SignatureProgress = ({ signaturesDocument }) => {
  const signed = signaturesDocument
    ?.filter((row) => row?.status === 'signed')
    ?.map((row) => row?.user?.professionalFullName)
    ?.join(', ');

  const notSigned = signaturesDocument
    .filter((row) => row?.status !== 'signed')
    ?.map((row) => row?.user?.professionalFullName)
    ?.join(', ');

  if (isEmpty(signed) && isEmpty(notSigned)) return null;

  return (
    <div>
      {!isEmpty(signed) && (
        <p className="text-sm font-500">
          Providers that signed: <i>{signed}</i>
        </p>
      )}
      {!isEmpty(notSigned) && (
        <p className="text-sm font-500">
          Providers that need to sign: <i>{notSigned}</i>
        </p>
      )}
    </div>
  );
};

export default ProviderSignature;
